import { useTranslation } from '@/i18n';

import { FC, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';

import { useSelector } from '@/api/state';
import CustomAutomations from '@/ui/components/customAutomations/CustomAutomations';
import DefaultAutomations from '@/ui/components/defaultAutomations/DefaultAutomations';
import { getUserRoles } from '@/utils/stateAccessors';
import DataIndicatorCard from '@payaca/components/dataIndicatorCard/DataIndicatorCard';
import { AutomationsPermissions } from '@payaca/permissions/automations/automations.permissions';
import { userHasRequiredPermission } from '@payaca/permissions/permissions.utils';
import { getKeyIndicatorsData } from '@payaca/store/analytics/analyticsActions';
import { requestGetAccountPipelines } from '@payaca/store/pipelines/pipelineActions';
import {
  KeyIndicatorData,
  KeyIndicatorType,
} from '@payaca/types/analyticsTypes';
import moment from 'moment-timezone';
import QueryString from 'qs';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import TabbedContentPageWrapper from '../pageWrappers/tabbedContentPageWrapper/TabbedContentPageWrapper';
import './AutomationsPage.sass';

type AutomationCategory = 'default' | 'custom';

const AutomationsPage: FC = () => {
  const translate = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();

  const { tab } = QueryString.parse(location.search, {
    ignoreQueryPrefix: true,
  });

  const [selectedCategory, setSelectedCategory] = useState<AutomationCategory>(
    tab == 'custom' ? 'custom' : 'default'
  );

  useEffect(() => {
    dispatch(requestGetAccountPipelines());
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, []);

  const userRoles = useSelector(getUserRoles);

  const accountHasCustomAutomations = userHasRequiredPermission(userRoles, [
    AutomationsPermissions.EDIT_AUTOMATION,
  ]);

  return (
    <>
      <Helmet title={translate('pages.automations.title')} />
      <TabbedContentPageWrapper
        className="automations-page"
        tabbedContentConfig={{
          tabs: [
            {
              label: 'default',
              content: <DefaultAutomations />,
              title: 'Templates',
            },
            ...(accountHasCustomAutomations
              ? [
                  {
                    label: 'custom',
                    content: <CustomAutomations />,
                    title: 'Custom',
                  },
                ]
              : []),
          ],
          activeTabLabel: selectedCategory,
          onChangeTab: (label) => {
            history.push(`/automations?tab=${label}`);
            setSelectedCategory(label as AutomationCategory);
          },
        }}
      >
        <div className="p-4">
          <AutomationKeyIndicatorsData />
        </div>
      </TabbedContentPageWrapper>
    </>
  );
};

export default AutomationsPage;

const AutomationKeyIndicatorsData: FC = () => {
  const dispatch = useDispatch();

  const [keyIndicatorsData, setKeyIndicatorsData] = useState<
    KeyIndicatorData<KeyIndicatorType>[]
  >([]);

  useEffect(() => {
    dispatch(
      getKeyIndicatorsData.request({
        indicatorTypes: [
          KeyIndicatorType.AUTOMATION_SMS_NOTIFICATIONS,
          KeyIndicatorType.AUTOMATION_EMAIL_NOTIFICATIONS,
        ],
        callback: setKeyIndicatorsData,
      })
    );
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, []);

  const smsNotificationIndicatorData = keyIndicatorsData?.find(
    (x) => x.type == KeyIndicatorType.AUTOMATION_SMS_NOTIFICATIONS
  );

  const emailNotificationIndicatorData = keyIndicatorsData?.find(
    (x) => x.type == KeyIndicatorType.AUTOMATION_EMAIL_NOTIFICATIONS
  );

  const indicatorCardWrapperClasses =
    'grow basis-0 max-w-[35rem] min-w-[25rem]';

  return (
    <div className="flex flex-row flex-wrap gap-8 w-full">
      {emailNotificationIndicatorData && (
        <div className={indicatorCardWrapperClasses}>
          <DataIndicatorCard
            title="Email automations"
            iconName="mail-05.3"
            periodLengthInDays={
              emailNotificationIndicatorData.periodLengthInDays
            }
            percentageChange={emailNotificationIndicatorData.percentageChange}
            sparkLineData={emailNotificationIndicatorData.sparkLineData}
            currentPeriod={emailNotificationIndicatorData.currentPeriod}
            CustomLabelledValue={({ value }) => {
              return (
                <>
                  <DataIndicatorCard.LabelledValue>
                    <DataIndicatorCard.ValueLabel>
                      Sent
                    </DataIndicatorCard.ValueLabel>
                    <DataIndicatorCard.Value>{value}</DataIndicatorCard.Value>
                  </DataIndicatorCard.LabelledValue>
                  <TimeSavedLabelledValue timeSavedInMinutes={value * 10} />
                </>
              );
            }}
          />
        </div>
      )}
      {smsNotificationIndicatorData && (
        <div className={indicatorCardWrapperClasses}>
          <DataIndicatorCard
            title="SMS automations"
            iconName="notification-message"
            periodLengthInDays={smsNotificationIndicatorData.periodLengthInDays}
            percentageChange={smsNotificationIndicatorData.percentageChange}
            sparkLineData={smsNotificationIndicatorData.sparkLineData}
            currentPeriod={smsNotificationIndicatorData.currentPeriod}
            CustomLabelledValue={({ value }) => {
              return (
                <>
                  <DataIndicatorCard.LabelledValue>
                    <DataIndicatorCard.ValueLabel>
                      Sent
                    </DataIndicatorCard.ValueLabel>
                    <DataIndicatorCard.Value>{value}</DataIndicatorCard.Value>
                  </DataIndicatorCard.LabelledValue>
                  <TimeSavedLabelledValue timeSavedInMinutes={value * 5} />
                </>
              );
            }}
          />
        </div>
      )}
    </div>
  );
};

const TimeSavedLabelledValue = ({
  timeSavedInMinutes,
}: {
  timeSavedInMinutes: number;
}) => {
  const duration = moment.duration(timeSavedInMinutes, 'minutes');

  const hours = duration.hours();
  const minutes = duration.minutes();

  if (!timeSavedInMinutes) return null;

  return (
    <DataIndicatorCard.LabelledValue>
      <DataIndicatorCard.ValueLabel>Time saved</DataIndicatorCard.ValueLabel>
      <DataIndicatorCard.Value>
        {!!hours && (
          <>
            {hours}
            <span className="text-base font-extralight"> hrs</span>{' '}
          </>
        )}{' '}
        {!!minutes && (
          <>
            {minutes}
            <span className="text-base font-extralight"> mins</span>{' '}
          </>
        )}{' '}
      </DataIndicatorCard.Value>
    </DataIndicatorCard.LabelledValue>
  );
};
